import { _js } from '@ifixit/utils/localize';
import Validation from 'Shared/validation';
import { setHideSubBox, onHideSubBoxChange } from 'Shared/ifixit_store';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';

declare const App: {
   recaptchaToken: string;
};

declare const Request: {
   AjaxIO: new (
      action: string,
      options: { onSuccess: (response: SignupResponse) => void }
   ) => {
      send: (email: string, recaptchaResponse: string, source?: string) => Promise<SignupResponse>;
   };
};

declare const fbq: (action: string, event: string, data: { content_name: string }) => void;

type SignupResponse = string | { result: string };

/**
 * Signup surfaces wired in this file; each maps to an Active Campaign
 * `Signup: <source>` tag. See active-campaign.json for the configured tags.
 */
type SignupSource =
   | 'Footer'
   | 'Homepage Banner'
   | 'Blog Post'
   | 'Valkyrie Banner'
   | 'Newsletter'
   | 'R2R LP';

interface MooToolsElement extends Element {
   addClass(className: string): void;
   removeClass(className: string): void;
   set(property: string, value: string): void;
   hide(): void;
}

abstract class NewsLetter {
   protected el!: MooToolsElement;

   constructor(el: Element | null) {
      if (!el) {
         return;
      }

      this.el = el as MooToolsElement;
      const form = this._getForm();
      if (form) {
         form.addEventListener('submit', this._onSubmit.bind(this));
      }

      onHideSubBoxChange((state: boolean) => {
         if (state) {
            this._successStyle();
         } else {
            this._setDefault();
         }
      });
   }

   protected _getEl(): MooToolsElement {
      return this.el;
   }

   protected _getIconEl(): MooToolsElement {
      const iconClass = 'newsletterIcon';

      let iconEl = this.el.querySelector(`.${iconClass}`) as MooToolsElement | null;

      if (iconEl) {
         return this.el;
      }

      iconEl = document.createElement('span') as unknown as MooToolsElement;
      this.el.append(iconEl);
      iconEl.addClass(iconClass);

      return iconEl;
   }

   protected _getTextEl(): MooToolsElement | null {
      return this.el.querySelector('p') as MooToolsElement | null;
   }

   protected _getEmailEl(): HTMLInputElement | null {
      const form = this._getForm();
      return form ? form.querySelector('input') : null;
   }

   protected _getForm(): HTMLFormElement | null {
      return this.el.querySelector('form');
   }

   protected _getViewLinkEls(): MooToolsElement[] | null {
      return null;
   }

   protected _getEmailSuccessIcon(): string {
      return '';
   }

   protected _getEmailSuccessText(): string {
      return _js(
         'You are a dashing and intelligent person. We look forward to getting to know you.'
      );
   }

   protected _getEmailDefaultText(): string {
      return _js('Learn something new every week:');
   }

   protected _getEmailErrorIcon(): string {
      return '';
   }

   protected _getEmailErrorText(): string {
      return _js('Invalid Email');
   }

   protected _getGenericErrorText(): string {
      return _js('An Unknown Error Occurred. Please try again.');
   }

   protected _getRecaptchaKey(): string {
      return App.recaptchaToken;
   }

   protected _getRecaptchaPlaceholder(): Element {
      const placeholderClass = 'newsletter-recaptcha';
      const emailEl = this._getEmailEl();
      let recaptchaPlaceholder = emailEl?.querySelector(`.${placeholderClass}`);

      if (recaptchaPlaceholder) {
         return recaptchaPlaceholder;
      }

      recaptchaPlaceholder = document.createElement('div');
      (recaptchaPlaceholder as MooToolsElement).addClass(placeholderClass);
      emailEl?.append(recaptchaPlaceholder);
      return recaptchaPlaceholder;
   }

   /**
    * ID used to build Active Campaign tag: `Signup: <source>`.
    * See active-campaign.json config file for list of supported tags.
    */
   protected _getSignupSource(): SignupSource | undefined {
      return undefined;
   }

   protected _signup(): Promise<SignupResponse> {
      return this._getRecaptchaPromise().then(recaptchaResponse => {
         const emailEl = this._getEmailEl();
         return new Request.AjaxIO('subscribe', {
            onSuccess: this._signupSuccess.bind(this),
         }).send(emailEl?.value || '', recaptchaResponse, this._getSignupSource());
      });
   }

   protected _signupWithLoading(): Promise<SignupResponse> {
      const signupPromise = this._signup();

      LoadingIndicator.withPromise(signupPromise, {
         pendingMessage: _js('Signing you up'),
         successMessage: _js('Signed up!'),
         failureMessage: _js('Something went wrong.'),
      });

      return signupPromise;
   }

   protected async _getRecaptchaPromise(): Promise<string> {
      const { Recaptcha } = await import('Shared/recaptcha');
      return Recaptcha.executeInvisible(
         this._getRecaptchaPlaceholder(),
         this._getRecaptchaKey(),
         'newsletter'
      );
   }

   protected _signupSuccess(response: SignupResponse): void {
      const result = typeof response === 'object' ? response.result : response;

      if (result === 'success') {
         this._setSuccess();
      } else {
         this._setError();
      }
   }

   protected _setDefault(): void {
      this.el.removeClass('error');
      this.el.removeClass('success');
      this._getIconEl().removeClass(this._getEmailSuccessIcon());
      this._getTextEl()?.set('text', this._getEmailDefaultText());
      this._getViewLinkEls()?.forEach(link => link.set('text', _js('Let me read it first!')));
   }

   protected _setSuccess(): void {
      setHideSubBox(true);
      let from = 'Footer';
      if (this.el.getAttribute('id') === 'newsletter-sub-box') {
         from = 'Newsletter page';
      }
      this._successStyle();

      this._successAnalytics(from);
   }

   protected _successStyle(): void {}

   protected _successAnalytics(from: string): void {
      trackAnalyticsCustomEvent({
         eventCategory: 'Newsletter',
         eventAction: `Newsletter - Form - ${from} - Submit`,
      });
      fbq('track', 'CompleteRegistration', { content_name: 'Newsletter' });
   }

   protected _setError(): void {
      this.el.addClass('error');
      this._getIconEl().addClass(this._getEmailErrorIcon());
      this._getTextEl()?.set('text', this._getEmailErrorText());
   }

   protected _validate(): boolean {
      const emailEl = this._getEmailEl();
      const isValid = Validation.email(emailEl?.value);
      if (!isValid) {
         this._setError();
      }
      return isValid;
   }

   protected _onSubmit(ev: Event): void {
      ev.stopPropagation();
      ev.preventDefault();
      this._setDefault();

      const isValid = this._validate();
      if (!isValid) {
         return;
      }

      void this._signupWithLoading();
   }
}

/**
 * Newsletter signup form used for WP content (i.e. homepage banner, blog post, campaign banner).
 *
 * @param el - The newsletter form element.
 * @param slug - The slug of the WP content.
 * @param signupSource - The source used to build Active Campaign tag: `Signup: <source>`.
 *                        WPNewsletter is used in multiple spots (e.g. homepage banner vs blog post),
 *                        so pass it through on construction.
 *                        See active-campaign.json config file for list of supported tags.
 */
class WPNewsletter extends NewsLetter {
   protected hiddenForm: HTMLFormElement;
   protected slug: string;
   protected signupSource: SignupSource | undefined;

   constructor(el: Element | null, slug: string, signupSource: SignupSource | undefined) {
      super(null);
      this.slug = slug;
      this.signupSource = signupSource;

      const hiddenForm = document.createElement('form');
      const hiddenInput = document.createElement('input');
      this.hiddenForm = hiddenForm;

      hiddenForm.style.display = 'none';
      hiddenForm.append(hiddenInput);
      document.body.append(hiddenForm);

      if (el) {
         this.el = el as MooToolsElement;
         const form = this._getForm();
         form?.addEventListener('submit', ev => {
            ev.stopPropagation();
            ev.preventDefault();

            const emailEl = this._getEmailEl();
            hiddenInput.value = emailEl?.value || '';
            hiddenForm.addEventListener('submit', this._onSubmit.bind(this));
            hiddenForm.dispatchEvent(new Event('submit', { cancelable: true }));
         });

         onHideSubBoxChange((state: boolean) => {
            if (state) {
               this._successStyle();
            } else {
               this._setDefault();
            }
         });
      }
   }

   protected override _getSignupSource(): SignupSource | undefined {
      return this.signupSource;
   }

   protected override _getIconEl(): MooToolsElement {
      // Don't append a visible icon element to the newsletter container.
      // The base class creates a <span> that renders as a black box.
      return this.hiddenForm as unknown as MooToolsElement;
   }

   protected override _getRecaptchaPlaceholder(): Element {
      const placeholderClass = 'newsletter-recaptcha';
      let recaptchaPlaceholder = this.hiddenForm.querySelector(`.${placeholderClass}`);

      if (recaptchaPlaceholder) {
         return recaptchaPlaceholder;
      }

      recaptchaPlaceholder = document.createElement('div');
      (recaptchaPlaceholder as MooToolsElement).addClass(placeholderClass);
      this.hiddenForm.append(recaptchaPlaceholder);
      return recaptchaPlaceholder;
   }

   protected override _successAnalytics(): void {
      trackAnalyticsCustomEvent({
         eventCategory: 'Newsletter',
         eventAction: `Newsletter - Form - ${this.slug} - Submit`,
      });
      fbq('track', 'CompleteRegistration', { content_name: 'Newsletter' });
   }

   protected override _getViewLinkEls(): MooToolsElement[] | null {
      return document.querySelectorAll(
         '.subscribe-form .form-footer-text .link-text'
      ) as unknown as MooToolsElement[];
   }

   protected override _successStyle(): void {
      this.el.removeClass('error');
      this.el.addClass('success');
      this._getIconEl().addClass(this._getEmailSuccessIcon());
      this._getTextEl()?.set('text', this._getEmailSuccessText());
      (this._getForm() as unknown as MooToolsElement)?.hide();
      this._getViewLinkEls()?.forEach(link => link.set('text', 'Let me read it!'));
   }
}

class FooterNewsletter extends NewsLetter {
   constructor(el: Element | null) {
      super(el);
   }

   protected override _getSignupSource(): SignupSource {
      return 'Footer';
   }

   protected override _setDefault(): void {}
}

/** The subscription box on the /Newsletter page. */
class NewsletterPageNewsletter extends NewsLetter {
   protected override _getSignupSource(): SignupSource {
      return 'Newsletter';
   }
}

export default NewsLetter;
export { WPNewsletter, FooterNewsletter, NewsletterPageNewsletter };
